// Audio spine: the one AudioContext, the master/SFX gain buses, the SFX sample
// player, and the persisted audio preferences (mute / SFX / music volumes).
//
// Self-contained leaf module (imports only core.js). main.js pulls it in with a
// side-effect import; interaction.js / main.js call `playSfx(...)` on user
// actions; settings.js drives the setters from the drawer; soundtrack.js reads
// the music prefs + the shared context. Audio prefs are a LOCAL setting — they
// live in their own localStorage key and are deliberately NOT part of the
// share/save payload.
//
// Browsers block all audio until a user gesture, so the context is created lazily
// and `resume()`d on the first pointer/key event; onFirstGesture() lets the
// soundtrack fade in at that same legal moment. SFX are cosmetic and never touch
// the seeded RNG (Math2.random), so they can't perturb reproducible replay.

const STORAGE_KEY = 'bonsai.audio.v1';

// --- Persisted preferences ---------------------------------------------------
const DEFAULTS = {
  muted: false,
  sfxEnabled: true,
  sfxVolume: 0.4,
  musicEnabled: true, // auto-fades in after the first gesture (see soundtrack.js)
  musicVolume: 0.5,
  ambientEnabled: true, // time-of-day wildlife loops (see ambient.js)
};

let prefs = loadPrefs();

function loadPrefs() {
  try {
    return { ...DEFAULTS, ...JSON.parse(localStorage.getItem(STORAGE_KEY) || '{}') };
  } catch {
    return { ...DEFAULTS };
  }
}

function persist() {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(prefs));
  } catch {
    /* storage full / unavailable — prefs just won't persist this session */
  }
}

// --- The SFX sample set ------------------------------------------------------
// Logical name -> one or more files (round-robin'd so repeats don't fatigue).
// Paths are root-relative (resolved against index.html, like every other asset).
const SFX_DEFS = {
  // Pruning shears closing (regular branches).
  shears: [
    'assets/sounds/shears-standard.mp3', 'assets/sounds/shears-snippy.mp3',
    'assets/sounds/shears-short-clip.mp3', 'assets/sounds/shears-quiet.mp3',
    'assets/sounds/shears-close-only.mp3',
  ],
  // Hand saw for the thick main branches (shears can't take those off).
  saw: [
    'assets/sounds/saw-3-stroke.mp3', 'assets/sounds/saw-4-stroke.mp3',
    'assets/sounds/saw-5-stroke.mp3', 'assets/sounds/saw-6-stroke.mp3',
  ],
  // Leafy branch rustle for a steady shaping gesture.
  rustle: ['assets/sounds/rustle-medium.wav', 'assets/sounds/rustle.ogg', 'assets/sounds/rustle-normal.wav'],
  // Punchy hit for a fast, abrupt shake of the branch (see shapeBranch).
  rustleShake: ['assets/sounds/rustle-short-hit.wav'],
  // Foliage dropping when a regular branch is cut off.
  rustleDrop: ['assets/sounds/rustle-medium.wav', 'assets/sounds/rustle-normal.wav'],
  // Heavier drop reserved for pruning a whole main branch.
  rustleDropMain: ['assets/sounds/rustle-loud.wav'],
  // New growth (grow / sub-branches).
  grow: ['assets/sounds/grow-standard.wav', 'assets/sounds/grow-medium.wav', 'assets/sounds/grow-short.wav'],
  // Bigger growth burst (main branches).
  growBig: ['assets/sounds/grow-long.wav'],
  // UI confirmation chime for save / share.
  sparkle: ['assets/sounds/menu-sparkle.mp3'],
  // The old tree being pulled out when a new bonsai is requested.
  pullOut: ['assets/sounds/pull-out.mp3'],
};

// Per-group base gain, so the louder samples sit back in the mix.
const SFX_GAIN = { shears: 0.96, saw: 0.3, rustle: 0.7, rustleShake: 0.8, rustleDrop: 0.5, rustleDropMain: 0.2, grow: 0.8, growBig: 0.85, sparkle: 0.85, pullOut: 0.99 };

const MAX_VOICES = 8; // cap simultaneous SFX so rapid clicks can't pile up

// --- Web Audio graph (lazy) --------------------------------------------------
let ctx = null;
let masterGain = null;
let sfxGain = null;
let unlocked = false;
let activeVoices = 0;
const buffers = new Map();   // url -> AudioBuffer (decoded once)
const lastPick = new Map();   // logical name -> last variant index played (to avoid repeats)
const firstGestureCbs = [];

// Create the context + bus graph on first need. Safe to call repeatedly.
function ensureContext() {
  if (ctx) return ctx;
  const AC = window.AudioContext || window.webkitAudioContext;
  if (!AC) return null;
  ctx = new AC();
  masterGain = ctx.createGain();
  masterGain.gain.value = prefs.muted ? 0 : 1;
  masterGain.connect(ctx.destination);
  sfxGain = ctx.createGain();
  sfxGain.gain.value = prefs.sfxEnabled ? prefs.sfxVolume : 0;
  sfxGain.connect(masterGain);
  preloadSfx();
  return ctx;
}

// Fetch + decode every curated SFX sample into the buffer cache (non-blocking).
async function preloadSfx() {
  const urls = [...new Set(Object.values(SFX_DEFS).flat())];
  await Promise.all(urls.map(async (url) => {
    if (buffers.has(url)) return;
    try {
      const data = await (await fetch(url)).arrayBuffer();
      buffers.set(url, await ctx.decodeAudioData(data));
    } catch {
      /* a missing/undecodable sample just stays silent */
    }
  }));
}

// --- First-gesture unlock ----------------------------------------------------
// One-shot: the first real user gesture creates+resumes the context (satisfying
// the autoplay policy) and notifies subscribers (the soundtrack fades in here).
function handleFirstGesture() {
  if (unlocked) return;
  unlocked = true;
  window.removeEventListener('pointerdown', handleFirstGesture);
  window.removeEventListener('keydown', handleFirstGesture);
  ensureContext();
  ctx?.resume?.();
  for (const cb of firstGestureCbs) {
    try { cb(); } catch { /* one bad subscriber shouldn't break the rest */ }
  }
}
window.addEventListener('pointerdown', handleFirstGesture);
window.addEventListener('keydown', handleFirstGesture);

// Register a callback for the first user gesture. Fires immediately if we're
// already unlocked.
export function onFirstGesture(cb) {
  if (unlocked) cb();
  else firstGestureCbs.push(cb);
}

// --- Page-visibility pause ----------------------------------------------------
// Users expect a backgrounded tab to fall silent. When the tab is hidden we
// suspend the WebAudio context (kills any in-flight SFX) and notify subscribers
// so the HTMLAudio-based loops (soundtrack.js / ambient.js) can pause; on return
// we resume. One-directional like the setters above: audio.js never imports back.
const visibilityCbs = [];
export function onVisibilityChange(cb) { visibilityCbs.push(cb); }
function handleVisibility() {
  const hidden = document.hidden;
  if (ctx) {
    if (hidden) ctx.suspend?.();
    else if (unlocked) ctx.resume?.();
  }
  for (const cb of visibilityCbs) { try { cb(hidden); } catch { /* ignore */ } }
}
document.addEventListener('visibilitychange', handleVisibility);

// Pick a random variant index for a group, avoiding an immediate repeat so the
// same sample never plays twice in a row (the main cause of "I keep hearing the
// same rustle"). Single-variant groups just return 0.
function pickVariant(name, len) {
  if (len <= 1) return 0;
  const prev = lastPick.get(name);
  let i = Math.floor(Math.random() * len);
  if (i === prev) i = (i + 1) % len; // nudge off a repeat (still uniform-ish)
  lastPick.set(name, i);
  return i;
}

// --- Play an SFX -------------------------------------------------------------
// Logical name -> a (round-robin'd) decoded sample, played through sfxGain with
// a little random pitch so repeats feel organic. `delay` (seconds) schedules the
// sample ahead — e.g. to let the foliage rustle land a beat after the shears cut.
// No-op until unlocked/enabled.
export function playSfx(name, { volume = 1, rate = 1, delay = 0 } = {}) {
  if (!unlocked || prefs.muted || !prefs.sfxEnabled) return;
  const group = SFX_DEFS[name];
  if (!group || !ctx || activeVoices >= MAX_VOICES) return;

  const i = pickVariant(name, group.length);
  const buf = buffers.get(group[i]);
  if (!buf) return; // not decoded yet

  const src = ctx.createBufferSource();
  src.buffer = buf;
  src.playbackRate.value = rate * (0.96 + Math.random() * 0.08); // ±4% pitch
  const g = ctx.createGain();
  g.gain.value = (SFX_GAIN[name] ?? 1) * volume;
  src.connect(g).connect(sfxGain);
  activeVoices++;
  src.onended = () => { activeVoices--; };
  src.start(ctx.currentTime + Math.max(0, delay));
}

// --- Preference setters (driven by the settings drawer) ----------------------
const musicCbs = [];
const ambientCbs = [];
// soundtrack.js / ambient.js subscribe so a one-directional dependency
// (soundtrack/ambient -> audio) stays clean: audio never imports either.
export function onMusicSettingChange(cb) { musicCbs.push(cb); }
export function onAmbientSettingChange(cb) { ambientCbs.push(cb); }
function notifyMusic() {
  for (const cb of musicCbs) { try { cb(prefs); } catch { /* ignore */ } }
}
function notifyAmbient() {
  for (const cb of ambientCbs) { try { cb(prefs); } catch { /* ignore */ } }
}

export function setMuted(on) {
  prefs.muted = !!on;
  if (masterGain) masterGain.gain.value = prefs.muted ? 0 : 1;
  persist();
  notifyMusic();
  notifyAmbient(); // mute also silences the HTMLAudio-based wildlife loops
}

export function setSfxEnabled(on) {
  prefs.sfxEnabled = !!on;
  if (sfxGain) sfxGain.gain.value = prefs.sfxEnabled ? prefs.sfxVolume : 0;
  persist();
}

export function setSfxVolume(v) {
  prefs.sfxVolume = clamp01(v);
  if (sfxGain && prefs.sfxEnabled) sfxGain.gain.value = prefs.sfxVolume;
  persist();
}

export function setMusicEnabled(on) {
  prefs.musicEnabled = !!on;
  persist();
  notifyMusic();
}

export function setMusicVolume(v) {
  prefs.musicVolume = clamp01(v);
  persist();
  notifyMusic();
}

export function setAmbientEnabled(on) {
  prefs.ambientEnabled = !!on;
  persist();
  notifyAmbient();
}

// Getters so the drawer can seed its controls from the authoritative store.
export function getPrefs() { return { ...prefs }; }

function clamp01(v) {
  v = Number(v);
  return Number.isFinite(v) ? Math.min(1, Math.max(0, v)) : 0;
}
